import { Trans, useTranslation } from 'react-i18next';
import Markdown from 'shared/components/Markdown';
import { SPACINGS } from 'shared/styles/variables';
import styled from 'styled-components';

import Spacer from 'design-system/components/Spacer';
import ImageMobilePending from 'images/pictographs/mobile-pending.svg';
import Button, { ButtonType } from 'inquiry/components/Button';
import CustomAssetWithFallback from 'inquiry/components/CustomAssetWithFallback';
import ThemedParagraph from 'inquiry/components/ThemedParagraph';
import ThemedTitle from 'inquiry/components/ThemedTitle';
import { useBaseStepStyleContext } from 'inquiry/context/BaseStepStyleContext';
import { colorCss } from 'lib/inquiryStyles';
import type {
  DeviceHandoffAssets,
  DeviceHandoffStartedPageLocalizations,
} from 'lib/interfaces/StepsConfig';
import CenteredPictograph from 'verify/components/CenteredPictograph';
import ThemedSvg from 'verify/components/ThemedSvg';
import { MIN_HEIGHTS } from 'verify/styles/variables';

const Header = styled('div')`
  min-height: ${MIN_HEIGHTS.header};
  margin-bottom: ${SPACINGS.base * 4}px;
`;

interface InquiryDeviceHandoffStartedViewProps {
  assets: DeviceHandoffAssets['device-handoff-started-page'];
  localizations?: DeviceHandoffStartedPageLocalizations;
  resumeOnDevice: () => void;
}

export default function InquiryDeviceHandoffStartedView({
  assets,
  localizations,
  resumeOnDevice,
}: InquiryDeviceHandoffStartedViewProps) {
  const [t] = useTranslation();
  const { base, buttons } = useBaseStepStyleContext();

  return (
    <>
      <CustomAssetWithFallback customImage={assets['header-pictograph']} alt='header pictograph' />
      <Header>
        <ThemedTitle styles={base?.titleStyle}>
          <Markdown>{localizations?.title ?? t('handed-off.title')}</Markdown>
        </ThemedTitle>
        <ThemedParagraph styles={base?.textStyle}>
          <Markdown>{localizations?.prompt ?? t('handed-off.prompt')}</Markdown>
        </ThemedParagraph>
      </Header>
      <CenteredPictograph aria-label='return to original device pictograph'>
        <CustomAssetWithFallback customImage={assets['device-pictograph']} alt='device pictograph'>
          <ThemedSvg
            $primaryColor={colorCss(base?.fill?.color)}
            $highlightColor={colorCss(base?.fill?.highlightColor)}
            $backgroundColor={colorCss(base?.fill?.backgroundColor)}
            $strokeColor={colorCss(base?.stroke?.color)}
          >
            <ImageMobilePending />
          </ThemedSvg>
        </CustomAssetWithFallback>
      </CenteredPictograph>
      {localizations?.['btn-resume'] ? (
        <>
          <Spacer height={2} />
          <Button
            id='inquiry-device-handoff-started__resume-btn'
            onClick={resumeOnDevice}
            type='button'
            buttonType={ButtonType.Secondary}
            styles={buttons?.[ButtonType.Secondary]}
          >
            {localizations['btn-resume']}
          </Button>
        </>
      ) : (
        <ThemedParagraph styles={base?.textStyle}>
          <a id='device-handoff-started__resume-link' onClick={resumeOnDevice}>
            <Trans i18nKey='handed-off.btn-resume'>Fallback Resume Button</Trans>
          </a>
        </ThemedParagraph>
      )}
    </>
  );
}
